import React from 'react';
import PropTypes from 'prop-types';

import TextField from '@material-ui/core/TextField';
import InputAdornment from '@material-ui/core/InputAdornment';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';

import { withStyles } from '@material-ui/core/styles';
import NoSsr from '@material-ui/core/NoSsr';
import formStyles from '../../styles/styles.module.scss';
import styles from './styles/styles.module.scss';

const StyledTextField = withStyles({
  root: {
    display: 'flex',
    width: '100%',
    borderRadius: '16px',
    fontFamily: 'Proxima Nova, Arial, sans-serif !important',
    '& .MuiFormLabel-root': {
      fontFamily: 'Proxima Nova, Arial, sans-serif !important',
      lineHeight: '8px'
    },
    '& .MuiFormLabel-root.MuiFormLabel-filled': {
      marginTop: '4px'
    },
    '& .MuiOutlinedInput-input': {
      padding: '10px 16px',
      margin: '4px 0px',
      borderRadius: '8px',
      color: '#414141',
      fontFamily: 'Proxima Nova, Arial, sans-serif !important'
    },
    '& .MuiInputLabel-outlined': {
      color: '#b2aea7',
      zIndex: 0
    },
    '& .MuiOutlinedInput-multiline': {
      padding: '0px'
    },
    '&.verticalResize .MuiOutlinedInput-input': {
      resize: 'vertical',
    },
    '& input + fieldset': {
      borderRadius: '8px',
      color: '#767676',
      borderColor: '#e6e6e6'
    },
    '& input:valid:focus + fieldset': {
      borderLeftWidth: '2 !important',
      borderWidth: '2',
      borderColor: '#428A13 !important',
      color: '#428A13'
    },
    '& input:focus + fieldset': {
      color: '#428A13',
      borderColor: '#428A13 !important'
    },
    '&. .MuiFormHelperText-contained': {
      margin: '4px 14px'
    },
    '& .MuiFormLabel-root.Mui-error': {
      color: 'red !important'
    },
    '& :hover .MuiOutlinedInput-notchedOutline': {
      borderColor: '#428A13 !important'
    },
    '& .MuiOutlinedInput-root': {
      borderRadius: '8px'
    },
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
      color: '#428A13 !important',
      borderColor: '#428A13 !important'
    },
    '& .MuiFormLabel-root.Mui-focused': {
      color: '#428A13',
      marginTop: '4px'
    },
    '& .MuiFormLabel-root.Mui-focused.Mui-error': {
      color: 'red'
    },
    '& .MuiFormHelperText-root': {
      fontFamily: 'Proxima Nova, Arial, sans-serif !important',
      color: 'red !important'
    },
    '& .MuiOutlinedInput-root.Mui-error': {
      minHeight: '47px !important' // for mobile safari
    },
    '& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline': {
      fontFamily: 'Proxima Nova, Arial, sans-serif !important',
      borderColor: 'red !important',
      borderWidth: '2px',
      color: 'red !important'
    }
  }
})(TextField);

const SsrStyledTextField = ({ label }) => {
  return <div className={styles.ssrContainer}>{label}</div>;
};

const OutlinedTextField = ({
  adornment,
  autocomplete,
  changeHandler,
  className,
  enterKeyHandler,
  errorMessage,
  isDisabled,
  isError,
  isRequired,
  isSuccess,
  label,
  name,
  type,
  multiline,
  verticalResize,
  rows,
  value
}) => {
  const onKeyPress = event => {
    if (event.key === 'Enter') {
      enterKeyHandler(event);
    }
  };

  const successAdornment = isSuccess ? (
    <InputAdornment position="end">
      <CheckCircleIcon className={styles.successCheck} />
    </InputAdornment>
  ) : (
    <InputAdornment position="end">{adornment}</InputAdornment>
  );

  const hasError = errorMessage && errorMessage.length > 0;

  return (
    <NoSsr fallback={<SsrStyledTextField label={label} />}>
      <StyledTextField
        autoComplete={autocomplete}
        className={`${className} ${styles.container} ${isError ? formStyles.inputError : ''} ${multiline ? styles.multiline : ''} ${verticalResize ? "verticalResize" : ''}`}
        disabled={isDisabled}
        error={isError || hasError}
        fullWidth
        helperText={isError || hasError ? errorMessage : ''}
        InputProps={{
          endAdornment: successAdornment
        }}
        label={label}
        name={name}
        onChange={changeHandler}
        onKeyPress={onKeyPress}
        required={isRequired}
        type={type}
        value={value}
        variant="outlined"
        multiline={multiline}
        rows={rows}
      />
    </NoSsr>
  );
};

OutlinedTextField.defaultProps = {
  changeHandler: () => {},
  enterKeyHandler: () => {},
  value: '',
  label: '',
  isError: false,
  isRequired: false,
  errorMessage: '',
  adornment: <></>,
  multiline: false,
  verticalResize: false
};

OutlinedTextField.propTypes = {
  isRequired: PropTypes.bool,
  changeHandler: PropTypes.func,
  value: PropTypes.string,
  label: PropTypes.string,
  isError: PropTypes.bool,
  errorMessage: PropTypes.string,
  adornment: PropTypes.node,
  multiline: PropTypes.bool,
  verticalResize: PropTypes.bool,
  rows: PropTypes.number
};

export default OutlinedTextField;
